import React, { useState } from 'react'
import intl from 'react-intl-universal'
import math, { formatNumber } from '../../utils/math'
import CurrencySymbol from '../CurrencySymbol'
import BenefitIcon from '../Icon/BenefitIcon'
import PropTypes from 'prop-types'
import { replaceCDNDomain } from '../../utils'
import { getAttrUnitPrice, getMinAttrPrice, getProductBasePrice, getProductLineAmount, getPriceSource, getProductListPriceSource, isPackagePriceFree, shouldUsePackagePrice } from '../../utils/price'

// 价格类型常量
const PRICE_TYPES = {
    FIXED: 1,
    MARKET: 2,
    UNIT: 3,
    FREE: 4,
    FREE_HIDE: 5
}

// 价格类型文本映射
const PRICE_TYPE_LABELS = {
    [PRICE_TYPES.MARKET]: 'realTimePrice',
    [PRICE_TYPES.FREE]: 'free'
}

const PACKAGE_ICON_SIZE = 16

/** 套餐价低于原价（含免费）时展示权益图标 */
const hasPackageBenefit = (item) => {
    if (!shouldUsePackagePrice(item)) return false
    return Number(item.packagePrice) < Number(item.price)
}

const PackagePriceIcon = ({ product }) => {
    const [iconError, setIconError] = useState(false)
    if (!hasPackageBenefit(product)) return null

    const iconUrl = product.packageIcon && !iconError ? replaceCDNDomain(product.packageIcon) : ''
    return (
        <span className="package-price-icon" style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: PACKAGE_ICON_SIZE,
            height: PACKAGE_ICON_SIZE,
            lineHeight: 1
        }}>
            {iconUrl ? (
                <img
                    src={iconUrl}
                    alt=""
                    style={{ width: PACKAGE_ICON_SIZE, height: PACKAGE_ICON_SIZE, objectFit: 'contain', display: 'block' }}
                    onError={() => setIconError(true)}
                />
            ) : (
                <BenefitIcon width={PACKAGE_ICON_SIZE} height={PACKAGE_ICON_SIZE} color="var(--adm-color-primary)" />
            )}
        </span>
    )
}

/**
 * 计算产品最低价格
 * @param {Object} item - 产品对象
 * @returns {number} 最低价格
 */
const getListMinAmount = (item) => {
    if (!item) return 0

    const { attrs = [] } = item
    const minAttrPrice = getMinAttrPrice(attrs)
    const priceSource = getProductListPriceSource(item)
    if (isPackagePriceFree(priceSource)) return 0
    const specPrice = getProductBasePrice(priceSource)

    return math.add(minAttrPrice, specPrice)
}

const calculateMinPrice = (item) => {
    if (!item) return '0'

    const { attrs = [], specs = [] } = item
    const subAttrs = attrs.flatMap(attr => attr.subAttrList || [])
    const minAttrPrice = getMinAttrPrice(attrs)
    const priceSource = getProductListPriceSource(item)
    if (isPackagePriceFree(priceSource)) return '0'
    const specPrice = getProductBasePrice(priceSource)

    const maxAttrPrice = subAttrs.length ? Math.max(...subAttrs.map(getAttrUnitPrice)) : 0
    const maxSpecPrice = shouldUsePackagePrice(item)
        ? getProductBasePrice(item)
        : specs.length
            ? Math.max(...specs.map(spec => getProductBasePrice(spec)))
            : getProductBasePrice(item)

    const amount = math.add(minAttrPrice, specPrice)
    const maxPrice = math.add(maxAttrPrice, maxSpecPrice)
    if (amount === maxPrice) return formatNumber(amount)

    return `${formatNumber(amount)}`
}

const isListPackageFree = (item) => {
    if (!item) return false
    return isPackagePriceFree(getProductListPriceSource(item))
}

/**
 * 价格文本组件
 * @param {Object} props
 * @param {Object} props.product - 产品信息
 */
const PriceText = ({ product, from }) => {
    if (!product) return null

    const { currency, currencyCode, priceType, weightUnitName, weight } = product
    const sellingAmount = from === 'cart' ? getProductLineAmount(product, true) : getListMinAmount(product)
    const packageFree = from === 'cart'
        ? isPackagePriceFree(getPriceSource(product))
        : isListPackageFree(product)
    // 权益套餐免费只展示 0；普通免费价类型仍展示「免费」文案
    const showFreeLabel = priceType === PRICE_TYPES.FREE && !packageFree
    const packageIcon = from === 'cart' ? null : <PackagePriceIcon product={product} />
    const wrapPrice = (content) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1 }}>
            {content}
        </span>
    )

    // 渲染基础价格文本
    const basePriceText = wrapPrice(
        <>
            {packageIcon}
            <CurrencySymbol currency={currency || currencyCode} />
            {from === 'cart' ? formatNumber(sellingAmount) : calculateMinPrice(product)}
        </>
    )

    if (priceType === PRICE_TYPES.FREE_HIDE) {
        return null
    }
    if (priceType === PRICE_TYPES.MARKET) {
        return <>{intl.get(PRICE_TYPE_LABELS[PRICE_TYPES.MARKET]).d('时价')}</>
    }
    if (showFreeLabel) {
        return wrapPrice(
            <>
                {packageIcon}
                {intl.get(PRICE_TYPE_LABELS[PRICE_TYPES.FREE]).d('免费')}
            </>
        )
    }

    // 根据价格类型返回对应的显示文本
    switch (priceType) {
        case PRICE_TYPES.FIXED:
            return basePriceText
        case PRICE_TYPES.UNIT:
            return wrapPrice(
                <>
                    {packageIcon}
                    <CurrencySymbol currency={currency || currencyCode} />
                    {from === 'cart' ? formatNumber(sellingAmount) : calculateMinPrice(product)}
                    /<small>{weight}{weightUnitName}</small>
                </>
            )
        default:
            return basePriceText
    }
}

PriceText.propTypes = {
    product: PropTypes.shape({
        currency: PropTypes.string,
        price: PropTypes.number,
        priceType: PropTypes.number,
        weightUnitName: PropTypes.string,
        weight: PropTypes.number,
        attrs: PropTypes.array,
        specs: PropTypes.array
    })
}

export default React.memo(PriceText)
